import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { obtenerResumenProyecto, limpiarResumenProyecto } from '../../../actions/proyectos';

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const StatCard = ({ label, value, tone, nota }) => (
    <div className="col-md-3 col-6 mb-3">
        <div className="border rounded p-3 h-100">
            <div className="text-muted small">{label}</div>
            <div className={`fs-5 fw-bold${tone ? ` text-${tone}` : ''}`}>{value}</div>
            {nota && <div className="text-muted" style={{ fontSize: 11 }}>{nota}</div>}
        </div>
    </div>
);

/* Agrupa las filas de productos de órdenes de salida por producto, ya que el mismo producto puede
   venir en distintas órdenes con precios distintos (precio, precio_compra e incluso IVA). Cada
   monto se promedia ponderado por cantidad para poder presentar una sola fila por producto, y se
   marca cuando el valor real varía entre las órdenes que se agruparon.
   Nota: el backend (controladores/proyectos.js, listadoProductosOS) no incluye "precio_venta" —
   solo trae precio_compra, precio (unitario) e iva — así que esa columna no puede completarse
   hasta que se agregue ahí `precio_venta: p.precio_venta || 0`. */
const agruparProductosOrdenesSalida = (listado) => {
    const mapa = new Map();

    (listado || []).forEach((p) => {
        const idProducto = p?.producto?._id || 'sin-producto';
        const idUnidad = p?.unidad?._id || 'sin-unidad';
        const esDePaquete = !!p?.de_paquete;
        /* Se incluye de_paquete en la key: un mismo producto puede venderse suelto y, a la vez,
           venir como componente de un paquete — no deben mezclarse en una sola fila. */
        const key = `${idProducto}_${idUnidad}_${esDePaquete}`;

        const cantidad = Number(p?.cantidad || 0);
        const importe = Number(p?.precio_final || 0) * cantidad;
        const importePrecio = Number(p?.precio || 0) * cantidad;
        const importeCompra = Number(p?.precio_compra || 0) * cantidad;
        const iva = !!p?.iva;

        if (!mapa.has(key)) {
            mapa.set(key, {
                key,
                productoNombre: p?.producto?.nombre || 'Sin definir',
                unidadNombre: p?.unidad?.nombre || 'Sin unidad',
                esDePaquete,
                cantidad: 0,
                importe: 0,
                importePrecio: 0,
                importeCompra: 0,
                precioVaria: false,
                precioCompraVaria: false,
                ivaVaria: false,
                precioReferencia: p?.precio ?? null,
                precioCompraReferencia: p?.precio_compra ?? null,
                ivaReferencia: iva
            });
        }

        const fila = mapa.get(key);
        fila.cantidad += cantidad;
        fila.importe += importe;
        fila.importePrecio += importePrecio;
        fila.importeCompra += importeCompra;

        if (fila.precioReferencia !== null && Number(p?.precio) !== Number(fila.precioReferencia)) {
            fila.precioVaria = true;
        }
        if (fila.precioCompraReferencia !== null && Number(p?.precio_compra) !== Number(fila.precioCompraReferencia)) {
            fila.precioCompraVaria = true;
        }
        if (iva !== fila.ivaReferencia) {
            fila.ivaVaria = true;
        }
    });

    return Array.from(mapa.values()).map((fila) => ({
        ...fila,
        precioPromedio: fila.cantidad > 0 ? fila.importePrecio / fila.cantidad : 0,
        precioCompraPromedio: fila.cantidad > 0 ? fila.importeCompra / fila.cantidad : 0
    }));
};

const agruparProductosOrdenesDevolucion = (listado) => {
    const mapa = new Map();

    (listado || []).forEach((p) => {
        const idProducto = p?.producto?._id || 'sin-producto';
        const idUnidad = p?.unidad?._id || 'sin-unidad';
        const esDePaquete = !!p?.de_paquete;
        /* de_paquete en la key: aquí "producto" guarda el paquete completo cuando esDePaquete
           es true, así que no debe mezclarse con una fila de producto suelto. */
        const key = `${idProducto}_${idUnidad}_${esDePaquete}`;

        if (!mapa.has(key)) {
            mapa.set(key, {
                key,
                productoNombre: p?.producto?.nombre || 'Sin definir',
                unidadNombre: p?.unidad?.nombre || 'Sin unidad',
                esDePaquete,
                cantidad: 0
            });
        }

        mapa.get(key).cantidad += Number(p?.cantidad || 0);
    });

    return Array.from(mapa.values());
};

const TABS = [
    { value: 'productos', label: 'Productos entregados' },
    { value: 'devoluciones', label: 'Productos devueltos' },
    { value: 'conceptos_productos', label: 'Conceptos (productos)' },
    { value: 'conceptos_servicios', label: 'Conceptos (servicios)' }
];

const ResumenProyecto = ({ idProyecto }) => {
    const dispatch = useDispatch();

    const { resumen, listadosResumen, loadingResumen } = useSelector(state => state.proyectos);

    const [tabListado, setTabListado] = useState('productos');

    useEffect(() => {
        if (!idProyecto) return;

        dispatch(obtenerResumenProyecto(idProyecto));

        return () => {
            dispatch(limpiarResumenProyecto());
        };
    }, [dispatch, idProyecto]);

    const productosAgrupados = useMemo(
        () => agruparProductosOrdenesSalida(listadosResumen?.productos_ordenes_salida),
        [listadosResumen]
    );

    const devolucionesAgrupadas = useMemo(
        () => agruparProductosOrdenesDevolucion(listadosResumen?.productos_ordenes_devolucion),
        [listadosResumen]
    );

    const conceptosProductos = listadosResumen?.conceptos_productos || [];
    const conceptosServicios = listadosResumen?.conceptos_servicios || [];

    if (loadingResumen || !resumen) {
        return (
            <div className="text-muted py-4">
                {loadingResumen ? 'Cargando resumen...' : 'Sin información de resumen disponible.'}
            </div>
        );
    }

    const saldoEsPrepago = Number(resumen.saldo) < 0;

    return (
        <Fragment>
            <div className="row">
                <StatCard
                    label="Total productos"
                    value={formatMoney(Number(resumen.total_productos || 0) + Number(resumen.total_paquetes || 0))}
                    nota="Incluye productos individuales y paquetes"
                />
                <StatCard label="Total conceptos" value={formatMoney(resumen.total_conceptos)} />
                <StatCard label="Total general (facturado)" value={formatMoney(resumen.total_general)} />
                <StatCard label="Total general (costo)" value={formatMoney(resumen.total_gastado)} />
                <StatCard
                    label="Total devuelto"
                    value={formatMoney(resumen.total_devoluciones)}
                    tone={Number(resumen.total_devoluciones) > 0 ? 'danger' : undefined}
                    nota="Ya restado del total facturado"
                />
                <StatCard label="Total pagos recibidos" value={formatMoney(resumen.total_pagos)} />
                <StatCard
                    label={saldoEsPrepago ? 'Saldo (prepago / a favor del cliente)' : 'Saldo pendiente'}
                    value={formatMoney(Math.abs(resumen.saldo))}
                    tone={saldoEsPrepago ? 'info' : (Number(resumen.saldo) > 0 ? 'warning' : 'success')}
                />
                <StatCard
                    label="Utilidad real"
                    value={formatMoney(resumen.utilidad_real)}
                    tone={Number(resumen.utilidad_real) < 0 ? 'danger' : 'success'}
                />
            </div>

            {saldoEsPrepago && (
                <div className="alert alert-info">
                    El cliente ha pagado más de lo facturado hasta el momento (prepago / excedente).
                </div>
            )}

            <ul className="nav nav-pills mb-3 mt-2">
                {TABS.map((t) => (
                    <li className="nav-item" key={t.value}>
                        <button
                            type="button"
                            className={`nav-link${tabListado === t.value ? ' active' : ''}`}
                            onClick={() => setTabListado(t.value)}
                        >
                            {t.label}
                        </button>
                    </li>
                ))}
            </ul>

            {tabListado === 'productos' && (
                <div className="table-responsive mb-2">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="text-end">Cantidad</th>
                                <th>Unidad</th>
                                <th className="text-end">Precio compra</th>
                                <th className="text-end">Precio venta</th>
                                <th className="text-center">IVA</th>
                                <th className="text-end">Importe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productosAgrupados.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">Sin productos</td>
                                </tr>
                            )}

                            {productosAgrupados.map((p) => (
                                <tr key={p.key}>
                                    <td>
                                        {p.productoNombre}
                                        {p.esDePaquete && (
                                            <div><span className="badge bg-secondary" style={{ fontSize: 10 }}>Componente de paquete</span></div>
                                        )}
                                    </td>
                                    <td className="text-end">{formatNumber(p.cantidad)}</td>
                                    <td>{p.unidadNombre}</td>
                                    <td className="text-end">
                                        {p.esDePaquete ? (
                                            <span className="text-muted">No aplica</span>
                                        ) : (
                                            <>
                                                {formatMoney(p.precioCompraPromedio)}
                                                {p.precioCompraVaria && (
                                                    <span className="text-muted" style={{ fontSize: 11 }}> (promedio)</span>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {p.esDePaquete ? (
                                            <span className="text-muted">No aplica</span>
                                        ) : (
                                            <>
                                                {formatMoney(p.precioPromedio)}
                                                {p.precioVaria && (
                                                    <span className="text-muted" style={{ fontSize: 11 }}> (promedio, varía por orden)</span>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        {p.esDePaquete ? (
                                            <span className="text-muted">No aplica</span>
                                        ) : (p.ivaVaria ? 'Mixto' : (p.ivaReferencia ? 'Sí' : 'No'))}
                                    </td>
                                    <td className="text-end">
                                        {p.esDePaquete ? (
                                            <span className="text-muted">No aplica</span>
                                        ) : formatMoney(p.importe)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tabListado === 'devoluciones' && (
                <Fragment>
                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                        El monto de estas devoluciones ya se descontó de "Total productos" y "Total general" (ver "Total devuelto" arriba).
                    </div>
                    <div className="table-responsive mb-2">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th className="text-end">Cantidad devuelta</th>
                                    <th>Unidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {devolucionesAgrupadas.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="text-center text-muted">Sin devoluciones</td>
                                    </tr>
                                )}

                                {devolucionesAgrupadas.map((p) => (
                                    <tr key={p.key}>
                                        <td>
                                            {p.productoNombre}
                                            {p.esDePaquete && (
                                                <div><span className="badge bg-secondary" style={{ fontSize: 10 }}>Componente de paquete</span></div>
                                            )}
                                        </td>
                                        <td className="text-end">{formatNumber(p.cantidad)}</td>
                                        <td>{p.unidadNombre}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Fragment>
            )}

            {tabListado === 'conceptos_productos' && (
                <div className="table-responsive mb-2">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>Detalle</th>
                                <th className="text-end">Cantidad</th>
                                <th>Unidad</th>
                                <th className="text-end">Precio unitario</th>
                                <th className="text-center">IVA</th>
                                <th className="text-end">Importe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {conceptosProductos.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">Sin conceptos</td>
                                </tr>
                            )}

                            {conceptosProductos.map((c) => (
                                <tr key={c._id}>
                                    <td>{c.detalle}</td>
                                    <td className="text-end">{formatNumber(c.cantidad)}</td>
                                    <td>{c.unidad?.nombre || '—'}</td>
                                    <td className="text-end">{formatMoney(c.precio_unitario)}</td>
                                    <td className="text-center">{c.iva ? 'Sí' : 'No'}</td>
                                    <td className="text-end">{formatMoney(Number(c.cantidad || 0) * Number(c.precio_unitario || 0) * (c.iva ? 1.16 : 1))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {tabListado === 'conceptos_servicios' && (
                <div className="table-responsive mb-2">
                    <table className="table table-striped table-hover">
                        <thead>
                            <tr>
                                <th>Detalle</th>
                                <th className="text-end">Cantidad</th>
                                <th>Unidad</th>
                                <th className="text-end">Precio unitario</th>
                                <th className="text-center">IVA</th>
                                <th className="text-end">Importe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {conceptosServicios.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">Sin conceptos</td>
                                </tr>
                            )}

                            {conceptosServicios.map((c) => (
                                <tr key={c._id}>
                                    <td>{c.detalle}</td>
                                    <td className="text-end">{formatNumber(c.cantidad)}</td>
                                    <td>{c.unidad?.nombre || '—'}</td>
                                    <td className="text-end">{formatMoney(c.precio_unitario)}</td>
                                    <td className="text-center">{c.iva ? 'Sí' : 'No'}</td>
                                    <td className="text-end">{formatMoney(Number(c.cantidad || 0) * Number(c.precio_unitario || 0) * (c.iva ? 1.16 : 1))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Fragment>
    );
};

export default ResumenProyecto;
